export type Action = {
  type: string;
  payload?: any;
  error?: any;
  loading?: boolean | string;
};

export enum AttainmentRisk {
  PA_CRITICAL = "PA Critical",
  UA_CRITICAL = "UA Critical",
  PA_HIGH = "PA High",
  UA_HIGH = "UA High",
  PA_MODERATE = "PA Moderate",
  UA_MODERATE = "UA Moderate",
  PA_LOW = "PA Low",
  UA_LOW = "UA Low",
  PA_NEGLIGIBLE = "PA Negligible",
  UA_NEGLIGIBLE = "UA Negligible"
}

export type Audit = {
  attainmentRisk: AttainmentRisk;
  auditDate: Date;
  auditSubType: string;
  auditType: string;
  auditToolType: string;
  status: string;
  caseId: string;
  legalEntityName: string;
  premisesName: string;
  premisesId: string;
};

export type DHB = {
  dhbId: string;
  dhbName: string;
};

export type AuditStatusReport = {
  caseId: string;
  dhbName: string;
  dhbId: string;
  auditYear: string;
  plannedAuditYear: string;
  legalEntityName: string;
  premisesName: string;
  auditDate: Date;
  status: string;
  reportUrl: string;
  auditType: string;
  auditSubType: string;
  auditToolType: string;
};

export type ContactEvent = {
  premisesId?: string;
  legalEntityId?: string;
  title: string;
  description: string;
  supportingDocuments?: File[];
  type: string;
  subType: string;
  tempCaseId: string;
};

export interface IContactEventType {
  value: string;
  display: string;
  subType: string[];
}

export type ControlledDrug = {
  product: string;
  quantityLicensed: number;
  quantityImportedExported?: number | null;
};

export type ControlledDrugReport = {
  completedForm: File;
  legalEntityId: string;
  premisesId: string;
  reportMonth: string;
  reportType: ReportType;
  reportYear: string;
};

export enum CamStatus {
  RESPONSE_REQUIRED = "Response Required",
  ASSESSMENT_REQUIRED = "Assessment Required",
  ASSESSMENT_IN_PROGRESS = "Assessment in Progress",
  PEER_REVIEW_REQUIRED = "Peer Review Required",
  PEER_REVIEW_COMPLETED = "Peer Review Completed",
  DECISION_REQUIRED = "Decision Required",
  CLOSED_RESOLVED = "Closed Resolved",
  CLOSED_UNRESOLVED = "Closed Unresolved",
  ONLINE_RESPONSE_RECEIVED = "Online Response Received"
}

export type AuditReport = {
  caseId: string;
  legalEntityName: string;
  premisesName: string;
  auditDate: string;
  status: string;
  associatedCams: CAM[];
};

export type CamStatusReport = {
  caseId: string;
  legalEntityName: string;
  premisesName: string;
  auditDate: string;
  status: string;
  associatedCams: CAM[];
};

export type CorrectiveActionHistory = {
  correctiveActionAuthor: string;
  correctiveActionRequired: string;
  correctiveActionResponse?: string;
  responseReceivedDate: Date;
};

export type CAM = {
  attainmentRisk: AttainmentRisk;
  auditDate: Date;
  auditSubType: string;
  auditType: string;
  camStatus: CamStatus;
  caseId: string;
  correctiveActionHistory?: CorrectiveActionHistory[];
  correctiveActionRequired?: string;
  correctiveActionResponse?: string;
  criterion?: string;
  criterionId: string;
  legalEntityName: string;
  premisesname: string;
  finding?: string;
  furtherAction?: string;
  guidance?: string;
  reference?: string;
  responseDueDate: Date;
};

export type Premises = {
  emailAddress: string;
  faxNumber: string | null;
  cmAcmCaseIdentifier: string;
  phoneNumber: string;
  siteDescription?: string;
  parentLegalEntity: {
    cmAcmCaseIdentifier: string;
    legalEntityDescriptor: string;
    legalEntityName: string;
    premisesCount: number;
    viewable: boolean;
  };
  addressDetails: {
    streetAddress: string;
    streetAddressSuburbOrRDNumber: string | null;
    streetAddressTownOrCity: string;
    streetAddressPostCode: string;
    streetAddressCountry: string | null;
    streetAddressOtherDetails: string | null;
    postalAddress: string | null;
    postalAddressSuburbOrRDNumber: string | null;
    postalAddressTownOrCity: string | null;
    postalAddressPostCode: string | null;
    postalAddressCountry: string | null;
    postalAddressOtherDetails: string | null;
    postalAndStreetAddessSame: boolean;
  };
  premisesDescriptor: string;
  premisesName: string;
  regulatoryInstruments?: RegulatoryInstrument[];
  website: string | null;
};

export type LegalEntity = {
  associatedPremises: {
    cmAcmCaseIdentifier: string;
    premisesDescriptor: string;
    premisesName: string;
  }[];
  addressDetails: {
    streetAddress: string;
    streetAddressSuburbOrRDNumber: string | null;
    streetAddressTownOrCity: string;
    streetAddressPostCode: string;
    streetAddressCountry: string | null;
    streetAddressOtherDetails: string | null;
    postalAddress: string | null;
    postalAddressSuburbOrRDNumber: string | null;
    postalAddressTownOrCity: string | null;
    postalAddressPostCode: string | null;
    postalAddressCountry: string | null;
    postalAddressOtherDetails: string | null;
    postalAndStreetAddessSame: boolean;
  };
  cmAcmCaseIdentifier: string;
  emailAddress: string;
  faxNumber: string | null;
  legalEntityDescriptor: string;
  legalEntityName: string;
  phoneNumber: string;
  premisesCount: number;
  website: string | null;
  viewable: boolean;
};

export enum RegulatoryInstrumentStatus {
  ACTIVE = "Active",
  PROVISIONALLY_ACTIVE = "Provisionally Active",
  SUSPENSION_INITIATED = "Suspension Initiated",
  SURRENDER_INITIATED = "Surrender Initiated",
  ACTIVE_CONTINUING_IN_FORCE = "Active - Continuing in Force",
  APPLICATION_REQUIRED = "Application Required",
  APPLICATION_RECEIVED = "Application Received",
  ONLINE_APPLICATION_RECEIVED = "Online Application Received",
  IN_PROGRESS = "In Progress",
  ASSESSMENT_REQUIRED = "Assessment Required",
  PEER_REVIEW_COMPLETED = "Peer Review Completed",
  DECISION_REQUIRED = "Decision Required",
  RESPONSE_REQUIRED = "Response Required",
  MANUAL_NOTIFICATION_REQUIRED = "Manual Notification Required"
}

export enum PaymentStatus {
  Not_Issued = "Not Issued",
  Issued_Oracle_Pending = "Issued - Oracle Pending",
  Issued_Not_Paid = "Issued- Not Paid",
  Issued_Paid = "Issued Paid",
  Invoice_Cancelled = "Invoice Cancelled",
  Not_Issued_Full_Fee_Waiver = "Not Issued - Full Fee Waiver"
}

export const PaymentStatusMapper: any = {
  "Not Issued": "Not Required",
  "Issued - Oracle Pending": "Payment Required",
  "Issued- Not Paid": "Payment Required",
  "Issued Paid": "Payment Required",
  "Invoice Cancelled": "Not Required",
  "Not Issued - Full Fee Waiver": "Not Required"
};

export type CdImportExport = {
  productName: string;
  quantityLicenced: string;
  quantityLicenseeDeclared: string;
  product2Name: string;
  quantity2Licenced: string;
  quantityLicensee2Declared: string;
  product3Name: string;
  quantity3Licenced: string;
  quantityLicensee3Declared: string;
  product4Name: string;
  quantity4Licenced: string;
  quantityLicensee4Declared: string;
  actualImportExportDate: string;
};

export type RegulatoryInstrument = {
  applicationForm?: File;
  canBeAmended: "Yes" | "No" | null;
  expiryDate?: Date | null;
  riStartDate?: Date;
  cmAcmCaseIdentifier: string;
  importExportDate?: Date;
  hasInvoice: "Yes" | "No";
  issuedDate?: Date;
  riNotificationDate: Date;
  legalEntity?: string;
  riInvoiceStatus: PaymentStatus;
  premises?: string;
  renewalFormDownloadLink?: string;
  riStatus?: RegulatoryInstrumentStatus;
  riType: string;
  riNumber?: string;
  nhinumber?: string;
  subtype?: string;
  supportingDocuments?: File[];
  type: string;
  cdImportExport?: CdImportExport;
  importExportCaseID?: string;
  applicantReference?: string;
};

export type Resource = {
  cmAcmCaseIdentifier: string;
  resourceName: string;
  resourceType: string;
  resourceCategoryId: string;
  resourceStatus: string;
  resourceId: string;
  resourceDescription: string;
  resourceDocType: string;
  resourceURL: string;
  resourceNameCategory: string;
  resourceNameSubCategory: string;
};

export type ResourceCategory = {
  title: string;
  resources: Resource[];
};

export enum ReportType {
  CDAnnual = "CDAnnual",
  CDMonthly = "CDMonthly",
  HempAnnual = "HempAnnual",
  HempTesting = "HempTesting"
}

export const ReportTypeLabels = new Map<string, string>([
  [ReportType.CDAnnual, "Controlled Drugs Annual Stock Report"],
  [ReportType.CDMonthly, "Controlled Drugs Monthly Transaction Report"],
  [ReportType.HempAnnual, "Industrial Hemp Annual Report"],
  [ReportType.HempTesting, "Industrial Hemp Testing Report"]
]);

export interface RestrictionNoticeSearchResult {
  cmAcmCaseIdentifier: string;
  givenName: string;
  middleName: string | null;
  surname: string;
  nhinumber: string;
  restrictionNotices: RestrictionNotice[];
}

export interface RestrictionNoticeContact {
  name: string;
  address: string | null;
  phoneNumber: string;
  hpiCpn?: string;
  firstName?: string;
  lastName?: string;
}

export interface RestrictionNotice {
  cmAcmCaseIdentifier: string;
  dateIssued: string | null;
  controlledMedicines: string;
  rinumber: string | null;
  pharmacies: RestrictionNoticeContact[];
  otherServiceProviders: RestrictionNoticeContact[];
  prescribers: RestrictionNoticeContact[];
}

export enum Role {
  // Base user (Exists in WS02):
  USER = "ROLE_USER",

  // Base MCOLS user (Exists in Case Manager):
  MCOLS = "ROLE_MCOLS",

  // Resticted access roles:
  DHB = "ROLE_DHB",
  LEGAL_ENTITIES = "ROLE_LEGAL_ENTITIES",
  PREMISES = "ROLE_PREMISES",
  RESTRICTION_NOTICE = "ROLE_RESTRICTION_NOTICE",
  VERIFY_RI = "ROLE_VERIFY_RI"
}

export type User = {
  authorities?: { authority: Role }[];
  cmAcmCaseIdentifier: string;
  emailAddress: string;
  givenName: string;
  legalEntitiesCount: number;
  middleName: string | null;
  phoneNumber?: string;
  mobileNumber?: string;
  addressDetails: {
    streetAddress: string;
    streetAddressSuburbOrRDNumber: string | null;
    streetAddressTownOrCity: string;
    streetAddressPostCode: string;
    streetAddressCountry: string | null;
    streetAddressOtherDetails: string | null;
    postalAddress: string | null;
    postalAddressSuburbOrRDNumber: string | null;
    postalAddressTownOrCity: string | null;
    postalAddressPostCode: string | null;
    postalAddressCountry: string | null;
    postalAddressOtherDetails: string | null;
    postalAndStreetAddessSame: boolean;
  };
  premisesCount: number;
  surname: string;
};
